/**
 * Cooldown Storage Utility
 *
 * Persists cooldown state in localStorage to survive page reloads.
 * This prevents users from bypassing cooldowns by refreshing the page.
 */

const COOLDOWN_STORAGE_KEY = 'watermark_cooldown_end';

/**
 * Check if localStorage is available (SSR-safe)
 */
const isLocalStorageAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    const testKey = '__test_localStorage__';
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Save cooldown end time to localStorage
 * @param seconds - Number of seconds for cooldown duration
 */
export const saveCooldownEndTime = (seconds: number): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    const endTime = Date.now() + (seconds * 1000);
    window.localStorage.setItem(COOLDOWN_STORAGE_KEY, endTime.toString());
  } catch (error) {
    console.error('[Cooldown] Error saving cooldown to localStorage:', error);
  }
};

/**
 * Get remaining cooldown time in seconds
 * Returns 0 if cooldown has expired or doesn't exist
 */
export const getRemainingCooldownTime = (): number => {
  if (!isLocalStorageAvailable()) return 0;

  try {
    const endTimeStr = window.localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (!endTimeStr) return 0;

    const endTime = parseInt(endTimeStr, 10);
    if (isNaN(endTime)) return 0;

    const now = Date.now();
    if (now >= endTime) {
      // Cooldown expired, clean up
      clearCooldown();
      return 0;
    }

    // Return remaining time in seconds
    return Math.ceil((endTime - now) / 1000);
  } catch (error) {
    console.error('[Cooldown] Error reading cooldown from localStorage:', error);
    return 0;
  }
};

/**
 * Check if cooldown is currently active
 */
export const isCooldownActive = (): boolean => {
  return getRemainingCooldownTime() > 0;
};

/**
 * Clear cooldown from localStorage
 */
export const clearCooldown = (): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    window.localStorage.removeItem(COOLDOWN_STORAGE_KEY);
  } catch (error) {
    console.error('[Cooldown] Error clearing cooldown from localStorage:', error);
  }
};

/**
 * Initialize cooldown state on component mount
 * Returns the remaining cooldown time if any exists
 */
export const initializeCooldown = (): number => {
  return getRemainingCooldownTime();
};
